import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Images } from "../../../../../../../utils/imageConstants";
import { Api } from "../../../../../../../services/interceptor";
import { QUERY_KEY } from "../../../../../../../services/queryEndPoint";
import type {
  IPlayerStatsApiResponse,
  IBattingStats,
  IBowlingStats,
} from "../types/playerProfile";

interface FilterState {
  format: string | null;
  trophy: string | null;
  duration: string | null;
}

interface OverallTabProps {
  leftPlayerId?: string | null;
  rightPlayerId?: string | null;
}

interface PlayerStatsSelection {
  battingStats?: IBattingStats;
  bowlingStats?: IBowlingStats;
}

const PREFERRED_FORMAT_ORDER = ["TEST", "ODI", "T20", "T10", "HUNDRED"];

const getSafeNumber = (value?: number | null) => Number(value ?? 0);

const formatStatValue = (
  value?: number | null,
  options?: {
    decimals?: number;
    emptyFallback?: string;
    zeroFallback?: string;
  }
) => {
  const { decimals, emptyFallback = "-", zeroFallback } = options ?? {};

  if (value == null || Number.isNaN(value)) return emptyFallback;
  if (value === 0 && zeroFallback) return zeroFallback;
  if (decimals != null) return Number(value).toFixed(decimals);
  return Number.isInteger(value) ? String(value) : String(value);
};

const getAvailableFormats = (api?: IPlayerStatsApiResponse | null) => {
  if (!api || typeof api !== "object") return [];

  const keys = Object.keys(api).filter((key) => {
    const categories = api[key];
    return !!categories && typeof categories === "object";
  });

  return keys.sort((left, right) => {
    const leftIndex = PREFERRED_FORMAT_ORDER.indexOf(left.toUpperCase());
    const rightIndex = PREFERRED_FORMAT_ORDER.indexOf(right.toUpperCase());

    if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  });
};

const getAvailableCategories = (
  api: IPlayerStatsApiResponse | null | undefined,
  format: string | null
) => {
  if (!api || !format) return [];
  const categories = api[format];
  if (!categories || typeof categories !== "object") return [];
  return Object.keys(categories);
};

const getSelectedStats = (
  api: IPlayerStatsApiResponse | null | undefined,
  format: string | null,
  category: string | null
): PlayerStatsSelection => {
  if (!api || !format) return {};

  const formatData = api[format];
  if (!formatData || typeof formatData !== "object") return {};

  const availableCategories = Object.keys(formatData);
  const resolvedCategory =
    (category && formatData[category] ? category : null) ?? availableCategories[0];

  if (!resolvedCategory) return {};

  return formatData[resolvedCategory] ?? {};
};

const matchesPlayedValue = (batting?: IBattingStats, bowling?: IBowlingStats) => {
  const battingMatches = batting?.totalMatchesCount;
  const bowlingMatches = bowling?.totalMatchesCount;
  return battingMatches ?? bowlingMatches ?? null;
};

const bowlingAverageValue = (bowling?: IBowlingStats) => {
  const wickets = getSafeNumber(bowling?.totalWickets);
  const runsConceded = bowling?.totalRunsCenceded;

  if (!wickets || runsConceded == null) return null;
  return runsConceded / wickets;
};

const DEFAULT_FIELDING_ROWS = [
  { title: "CATCHES", leftValue: "-", rightValue: "-" },
  { title: "STUMPINGS", leftValue: "-", rightValue: "-" },
  { title: "RUN OUTS", leftValue: "-", rightValue: "-" },
];

const OverallTab = ({ leftPlayerId, rightPlayerId }: OverallTabProps) => {
  const [isLeftFilter, setIsLeftFilter] = useState(false);
  const [isRightFilter, setIsRightFilter] = useState(false);
  const [leftActiveSection, setLeftActiveSection] = useState<string>("format");
  const [rightActiveSection, setRightActiveSection] = useState<string>("format");
  const [leftFilterState, setLeftFilterState] = useState<FilterState>({
    format: null,
    trophy: null,
    duration: null,
  });
  const [rightFilterState, setRightFilterState] = useState<FilterState>({
    format: null,
    trophy: null,
    duration: null,
  });

  const leftFilterRef = useRef<HTMLDivElement>(null);
  const rightFilterRef = useRef<HTMLDivElement>(null);
  const leftFilterPanelId = useId();
  const rightFilterPanelId = useId();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        leftFilterRef.current &&
        !leftFilterRef.current.contains(event.target as Node)
      ) {
        setIsLeftFilter(false);
      }
      if (
        rightFilterRef.current &&
        !rightFilterRef.current.contains(event.target as Node)
      ) {
        setIsRightFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const leftStatsQuery = useQuery<IPlayerStatsApiResponse | null>({
    queryKey: [QUERY_KEY.getPlayerStats, leftPlayerId],
    enabled: !!leftPlayerId,
    queryFn: async () => {
      if (!leftPlayerId) return null;
      const res = await Api.get(QUERY_KEY.getPlayerStats, {
        params: { playerId: leftPlayerId },
      });
      return (res?.data ?? null) as IPlayerStatsApiResponse | null;
    },
  });

  const rightStatsQuery = useQuery<IPlayerStatsApiResponse | null>({
    queryKey: [QUERY_KEY.getPlayerStats, rightPlayerId],
    enabled: !!rightPlayerId,
    queryFn: async () => {
      if (!rightPlayerId) return null;
      const res = await Api.get(QUERY_KEY.getPlayerStats, {
        params: { playerId: rightPlayerId },
      });
      return (res?.data ?? null) as IPlayerStatsApiResponse | null;
    },
  });

  const leftFormats = useMemo(
    () => getAvailableFormats(leftStatsQuery.data),
    [leftStatsQuery.data]
  );
  const rightFormats = useMemo(
    () => getAvailableFormats(rightStatsQuery.data),
    [rightStatsQuery.data]
  );

  const leftCategories = useMemo(
    () => getAvailableCategories(leftStatsQuery.data, leftFilterState.format),
    [leftStatsQuery.data, leftFilterState.format]
  );
  const rightCategories = useMemo(
    () => getAvailableCategories(rightStatsQuery.data, rightFilterState.format),
    [rightStatsQuery.data, rightFilterState.format]
  );

  useEffect(() => {
    if (!leftFormats.length) return;
    setLeftFilterState((prev) => {
      const nextFormat = prev.format && leftFormats.includes(prev.format) ? prev.format : leftFormats[0];
      const nextCategories = getAvailableCategories(leftStatsQuery.data, nextFormat);
      const nextTrophy =
        prev.trophy && nextCategories.includes(prev.trophy) ? prev.trophy : nextCategories[0] ?? null;

      if (prev.format === nextFormat && prev.trophy === nextTrophy) return prev;
      return { ...prev, format: nextFormat, trophy: nextTrophy };
    });
  }, [leftFormats, leftStatsQuery.data]);

  useEffect(() => {
    if (!rightFormats.length) return;
    setRightFilterState((prev) => {
      const nextFormat = prev.format && rightFormats.includes(prev.format) ? prev.format : rightFormats[0];
      const nextCategories = getAvailableCategories(rightStatsQuery.data, nextFormat);
      const nextTrophy =
        prev.trophy && nextCategories.includes(prev.trophy) ? prev.trophy : nextCategories[0] ?? null;

      if (prev.format === nextFormat && prev.trophy === nextTrophy) return prev;
      return { ...prev, format: nextFormat, trophy: nextTrophy };
    });
  }, [rightFormats, rightStatsQuery.data]);

  const leftSelectedStats = useMemo(
    () => getSelectedStats(leftStatsQuery.data, leftFilterState.format, leftFilterState.trophy),
    [leftStatsQuery.data, leftFilterState.format, leftFilterState.trophy]
  );
  const rightSelectedStats = useMemo(
    () => getSelectedStats(rightStatsQuery.data, rightFilterState.format, rightFilterState.trophy),
    [rightStatsQuery.data, rightFilterState.format, rightFilterState.trophy]
  );

  const handleLeftFilterToggle = useCallback(() => {
    setIsLeftFilter((v) => !v);
  }, []);

  const handleRightFilterToggle = useCallback(() => {
    setIsRightFilter((v) => !v);
  }, []);

  const handleLeftFormatSelect = (value: string) => {
    const nextCategories = getAvailableCategories(leftStatsQuery.data, value);
    setLeftFilterState((prev) => ({
      ...prev,
      format: value,
      trophy: nextCategories[0] ?? null,
    }));
    setLeftActiveSection(nextCategories.length ? "trophy" : "format");
  };

  const handleLeftTrophySelect = (value: string) => {
    setLeftFilterState((prev) => ({ ...prev, trophy: value }));
  };

  const handleRightFormatSelect = (value: string) => {
    const nextCategories = getAvailableCategories(rightStatsQuery.data, value);
    setRightFilterState((prev) => ({
      ...prev,
      format: value,
      trophy: nextCategories[0] ?? null,
    }));
    setRightActiveSection(nextCategories.length ? "trophy" : "format");
  };

  const handleRightTrophySelect = (value: string) => {
    setRightFilterState((prev) => ({ ...prev, trophy: value }));
  };

  const handleLeftReset = () => {
    setLeftFilterState({
      format: leftFormats[0] ?? null,
      trophy: getAvailableCategories(leftStatsQuery.data, leftFormats[0] ?? null)[0] ?? null,
      duration: null,
    });
    setLeftActiveSection("format");
  };

  const handleRightReset = () => {
    setRightFilterState({
      format: rightFormats[0] ?? null,
      trophy: getAvailableCategories(rightStatsQuery.data, rightFormats[0] ?? null)[0] ?? null,
      duration: null,
    });
    setRightActiveSection("format");
  };

  const handleLeftApply = () => {
    setIsLeftFilter(false);
  };

  const handleRightApply = () => {
    setIsRightFilter(false);
  };

  const isLeftApplyDisabled = !leftFilterState.format;
  const isRightApplyDisabled = !rightFilterState.format;
  const isLeftTrophyDisabled = leftCategories.length === 0;
  const isRightTrophyDisabled = rightCategories.length === 0;

  const matchesRows = useMemo(
    () => [
      {
        title: "MATCHES PLAYED",
        leftValue: formatStatValue(
          matchesPlayedValue(leftSelectedStats.battingStats, leftSelectedStats.bowlingStats)
        ),
        rightValue: formatStatValue(
          matchesPlayedValue(rightSelectedStats.battingStats, rightSelectedStats.bowlingStats)
        ),
      },
    ],
    [leftSelectedStats, rightSelectedStats]
  );

  const battingRows = useMemo(
    () => [
      {
        title: "INNS",
        leftValue: formatStatValue(leftSelectedStats.battingStats?.totalInningsCount),
        rightValue: formatStatValue(rightSelectedStats.battingStats?.totalInningsCount),
      },
      {
        title: "RUNS",
        leftValue: formatStatValue(leftSelectedStats.battingStats?.totalRunsScored),
        rightValue: formatStatValue(rightSelectedStats.battingStats?.totalRunsScored),
      },
      {
        title: "BATTING AVG",
        leftValue: formatStatValue(leftSelectedStats.battingStats?.average, { decimals: 2 }),
        rightValue: formatStatValue(rightSelectedStats.battingStats?.average, { decimals: 2 }),
      },
      {
        title: "STRIKE RATE",
        leftValue: formatStatValue(leftSelectedStats.battingStats?.strikeRate, { decimals: 2 }),
        rightValue: formatStatValue(rightSelectedStats.battingStats?.strikeRate, { decimals: 2 }),
      },
      {
        title: "4'S",
        leftValue: formatStatValue(leftSelectedStats.battingStats?.foursCount),
        rightValue: formatStatValue(rightSelectedStats.battingStats?.foursCount),
      },
      {
        title: "6'S",
        leftValue: formatStatValue(leftSelectedStats.battingStats?.sixesCount),
        rightValue: formatStatValue(rightSelectedStats.battingStats?.sixesCount),
      },
      {
        title: "HIGHEST SCORE",
        leftValue: formatStatValue(leftSelectedStats.battingStats?.highestScore),
        rightValue: formatStatValue(rightSelectedStats.battingStats?.highestScore),
      },
    ],
    [leftSelectedStats, rightSelectedStats]
  );

  const bowlingRows = useMemo(
    () => [
      {
        title: "INNS",
        leftValue: formatStatValue(leftSelectedStats.bowlingStats?.totalInningsCount),
        rightValue: formatStatValue(rightSelectedStats.bowlingStats?.totalInningsCount),
      },
      {
        title: "WICKETS",
        leftValue: formatStatValue(leftSelectedStats.bowlingStats?.totalWickets),
        rightValue: formatStatValue(rightSelectedStats.bowlingStats?.totalWickets),
      },
      {
        title: "OVERS",
        leftValue: formatStatValue(leftSelectedStats.bowlingStats?.totalOvers, { decimals: 1 }),
        rightValue: formatStatValue(rightSelectedStats.bowlingStats?.totalOvers, { decimals: 1 }),
      },
      {
        title: "ECONOMY",
        leftValue: formatStatValue(leftSelectedStats.bowlingStats?.economy, { decimals: 2 }),
        rightValue: formatStatValue(rightSelectedStats.bowlingStats?.economy, { decimals: 2 }),
      },
      {
        title: "BOWLING AVG",
        leftValue: formatStatValue(bowlingAverageValue(leftSelectedStats.bowlingStats), { decimals: 2 }),
        rightValue: formatStatValue(bowlingAverageValue(rightSelectedStats.bowlingStats), { decimals: 2 }),
      },
      {
        title: "MAIDENS",
        leftValue: formatStatValue(leftSelectedStats.bowlingStats?.totalMaidenOverCount),
        rightValue: formatStatValue(rightSelectedStats.bowlingStats?.totalMaidenOverCount),
      },
      {
        title: "5W",
        leftValue: formatStatValue(leftSelectedStats.bowlingStats?.fiveWicketsCount),
        rightValue: formatStatValue(rightSelectedStats.bowlingStats?.fiveWicketsCount),
      },
      {
        title: "10W",
        leftValue: formatStatValue(leftSelectedStats.bowlingStats?.tenWicketsCount),
        rightValue: formatStatValue(rightSelectedStats.bowlingStats?.tenWicketsCount),
      },
    ],
    [leftSelectedStats, rightSelectedStats]
  );

  const renderStatSection = (
    heading: string,
    rows: Array<{ title: string; leftValue: string; rightValue: string }>
  ) => (
    <section className="matches-wrapper" aria-label={heading}>
      <h3 className="match-text">{heading}</h3>
      <div className="match-comparison-wrap">
        {rows.map((row) => (
          <div className="match-comparison" role="group" aria-label={`${heading} ${row.title}`} key={`${heading}-${row.title}`}>
            <div className="left-number-wrapper">
              <p className="number">{row.leftValue}</p>
            </div>
            <p className="title">{row.title}</p>
            <div className="right-number-wrapper">
              <p className="number">{row.rightValue}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  return (
    <section className="overall-tabs-wrap" aria-label="Overall statistics">
      <div className="filter-wrapper" role="group" aria-label="Filters">
        <div className="filter-wrap" ref={leftFilterRef}>
          <div className="chip-wrapper">
            <p className="chip-text">{leftFilterState.format || "Format"}</p>
          </div>
          <img
            alt=""
            src={Images.analysis_dropdown}
            aria-hidden="true"
            onClick={handleLeftFilterToggle}
            className="dropdown-icon"
          />

          {isLeftFilter && (
            <section
              id={leftFilterPanelId}
              className="filter-compare left-compare-wrap"
              role="dialog"
              aria-label="Filter options"
              aria-modal="false"
            >
              <div className="main-filter-wrap">
                <div className="filter-compare-child">
                  <div className="left-selection">
                    <div className="select-type-parent">
                      <div
                        className={`select-type ${leftActiveSection === "format" ? "active" : ""} ${leftFilterState.format ? "has-selection" : ""}`}
                        onClick={() => setLeftActiveSection("format")}
                      >
                        <p className="format">Format</p>
                        {leftFilterState.format && <span className="selection-dot"></span>}
                      </div>
                      <div
                        className={`select-type ${leftActiveSection === "trophy" ? "active" : ""} ${isLeftTrophyDisabled ? "disabled" : ""} ${leftFilterState.trophy ? "has-selection" : ""}`}
                        onClick={() => !isLeftTrophyDisabled && setLeftActiveSection("trophy")}
                      >
                        <p className="trophy">Category</p>
                        {leftFilterState.trophy && <span className="selection-dot"></span>}
                      </div>
                    </div>
                  </div>
                  <div className="right-radio-parent">
                    {leftActiveSection === "format" &&
                      leftFormats.map((format) => (
                        <label className="radio-inputs" key={format}>
                          <input
                            type="radio"
                            name="left-format"
                            checked={leftFilterState.format === format}
                            onChange={() => handleLeftFormatSelect(format)}
                          />
                          <p>{format}</p>
                        </label>
                      ))}
                    {leftActiveSection === "trophy" && !isLeftTrophyDisabled &&
                      leftCategories.map((category) => (
                        <label className="radio-inputs" key={category}>
                          <input
                            type="radio"
                            name="left-category"
                            checked={leftFilterState.trophy === category}
                            onChange={() => handleLeftTrophySelect(category)}
                          />
                          <p>{category}</p>
                        </label>
                      ))}
                  </div>
                </div>
                <div className="filter-footer">
                  <button
                    type="button"
                    className="reset-button"
                    onClick={handleLeftReset}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className={`apply-button ${isLeftApplyDisabled ? "disabled" : ""}`}
                    onClick={handleLeftApply}
                    disabled={isLeftApplyDisabled}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="filter-wrap" ref={rightFilterRef}>
          <div className="chip-wrapper">
            <p className="chip-text">{rightFilterState.format || "Format"}</p>
          </div>
          <img
            alt=""
            src={Images.analysis_dropdown}
            aria-hidden="true"
            onClick={handleRightFilterToggle}
            className="dropdown-icon"
          />

          {isRightFilter && (
            <section
              id={rightFilterPanelId}
              className="filter-compare"
              role="dialog"
              aria-label="Filter options"
              aria-modal="false"
            >
              <div className="main-filter-wrap">
                <div className="filter-compare-child">
                  <div className="left-selection">
                    <div className="select-type-parent">
                      <div
                        className={`select-type ${rightActiveSection === "format" ? "active" : ""} ${rightFilterState.format ? "has-selection" : ""}`}
                        onClick={() => setRightActiveSection("format")}
                      >
                        <p className="format">Format</p>
                        {rightFilterState.format && <span className="selection-dot"></span>}
                      </div>
                      <div
                        className={`select-type ${rightActiveSection === "trophy" ? "active" : ""} ${isRightTrophyDisabled ? "disabled" : ""} ${rightFilterState.trophy ? "has-selection" : ""}`}
                        onClick={() => !isRightTrophyDisabled && setRightActiveSection("trophy")}
                      >
                        <p className="trophy">Category</p>
                        {rightFilterState.trophy && <span className="selection-dot"></span>}
                      </div>
                    </div>
                  </div>
                  <div className="right-radio-parent">
                    {rightActiveSection === "format" &&
                      rightFormats.map((format) => (
                        <label className="radio-inputs" key={format}>
                          <input
                            type="radio"
                            name="right-format"
                            checked={rightFilterState.format === format}
                            onChange={() => handleRightFormatSelect(format)}
                          />
                          <p>{format}</p>
                        </label>
                      ))}
                    {rightActiveSection === "trophy" && !isRightTrophyDisabled &&
                      rightCategories.map((category) => (
                        <label className="radio-inputs" key={category}>
                          <input
                            type="radio"
                            name="right-category"
                            checked={rightFilterState.trophy === category}
                            onChange={() => handleRightTrophySelect(category)}
                          />
                          <p>{category}</p>
                        </label>
                      ))}
                  </div>
                </div>
                <div className="filter-footer">
                  <button
                    type="button"
                    className="reset-button"
                    onClick={handleRightReset}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className={`apply-button ${isRightApplyDisabled ? "disabled" : ""}`}
                    onClick={handleRightApply}
                    disabled={isRightApplyDisabled}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {!leftPlayerId || !rightPlayerId ? (
        <section className="matches-wrapper" aria-label="Overall stats unavailable">
          <div className="match-comparison-wrap">
            <div className="match-comparison" role="status" aria-live="polite">
              <p className="title">Select both players to compare their overall stats.</p>
            </div>
          </div>
        </section>
      ) : leftStatsQuery.isLoading || rightStatsQuery.isLoading ? (
        <section className="matches-wrapper" aria-label="Loading overall stats">
          <div className="match-comparison-wrap">
            <div className="match-comparison" role="status" aria-live="polite">
              <p className="title">Loading overall player stats...</p>
            </div>
          </div>
        </section>
      ) : leftStatsQuery.isError || rightStatsQuery.isError ? (
        <section className="matches-wrapper" aria-label="Overall stats error">
          <div className="match-comparison-wrap">
            <div className="match-comparison" role="alert">
              <p className="title">Unable to load overall stats right now.</p>
            </div>
          </div>
        </section>
      ) : (
        <>
          {renderStatSection("MATCHES", matchesRows)}
          {renderStatSection("BATTING", battingRows)}
          {renderStatSection("BOWLING", bowlingRows)}
          {renderStatSection("FIELDING", DEFAULT_FIELDING_ROWS)}
        </>
      )}
    </section>
  );
};

export default OverallTab;
